import type { ServiceTypeEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { queryOptionsGetFeatures } from '../api/shared/settingsApi';
import { NavigateWithParams } from '../components/NavigateWithParams';
import { SingleQueryResults } from '../components/QueryResults/SingleQueryResults';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { STATEMENT_ADDON_CHECKS } from '../hooks/useRoyaltiesRouteFlags';
import {
  type RoyaltiesRegion,
  type RoyaltiesSection,
  royaltiesAnzLayoutConfig,
  royaltiesWorldLayoutConfig,
} from '../layout/royaltiesLayoutConfig';
import { DISBURSEMENT_SERVICE_TYPES } from '../pages/v3/Payments/Disbursements';
import { RESIDUAL_SERVICE_TYPES } from '../pages/v3/Payments/Residuals';
import { REGION_SERVICE_TYPE } from '../pages/v3/Payments/RoyaltiesListTable';

type StatementPage = {
  /** A route flag on top of the payments flag; the page is skipped while it's off. */
  featureFlag?: string;
  /** The addon check of the page's route guard; the page is skipped when it fails. */
  isEnabled?: (workspace?: Workspace | null) => boolean;
  path: string;
  serviceTypes: readonly ServiceTypeEnum[];
};

const EARNINGS_STATEMENTS: RoyaltiesSection = 'earnings-statements';

/** Keyed by region, so a new region fails to compile until it's added here. */
const ROYALTIES_REGION_ROUTES = {
  anz: {
    featureFlag: FEATURE_FLAGS.MSR3_ROYALTIES_ANZ_ROUTE,
    layoutConfig: royaltiesAnzLayoutConfig,
  },
  world: {
    featureFlag: FEATURE_FLAGS.MSR3_ROYALTIES_WORLD_ROUTE,
    layoutConfig: royaltiesWorldLayoutConfig,
  },
} satisfies Record<
  RoyaltiesRegion,
  { featureFlag: string; layoutConfig: { basePathSegment: string } }
>;

const DISBURSEMENTS_PAGE: StatementPage = {
  path: 'disbursements',
  isEnabled: STATEMENT_ADDON_CHECKS.disbursements,
  serviceTypes: DISBURSEMENT_SERVICE_TYPES,
};

const RESIDUALS_PAGE: StatementPage = {
  path: 'residuals',
  isEnabled: STATEMENT_ADDON_CHECKS.residuals,
  serviceTypes: RESIDUAL_SERVICE_TYPES,
};

/** Where a link with no matching page lands, in order of preference. */
const FALLBACK_PAGES = [DISBURSEMENTS_PAGE, RESIDUALS_PAGE];

/** Historic Royalties, for a workspace with neither Disbursements nor Residuals. */
const LAST_RESORT_PATH = 'royalties';

const STATEMENT_PAGES: StatementPage[] = [
  DISBURSEMENTS_PAGE,
  RESIDUALS_PAGE,
  ...(Object.keys(ROYALTIES_REGION_ROUTES) as RoyaltiesRegion[]).map(
    (region) => ({
      path: `${ROYALTIES_REGION_ROUTES[region].layoutConfig.basePathSegment}/${EARNINGS_STATEMENTS}`,
      featureFlag: ROYALTIES_REGION_ROUTES[region].featureFlag,
      serviceTypes: [REGION_SERVICE_TYPE[region]],
    }),
  ),
];

/**
 * Payout emails link to `/payments?serviceType=`, so the backend needs no MSR3 routes.
 * This sends them to the page listing that service type, and anything else to the
 * first statements page the workspace's addons open (Disbursements, then Residuals,
 * then Historic Royalties), so a link never lands on a page the workspace cannot see.
 */
export const PaymentsRedirect = () => {
  const [searchParams] = useSearchParams();
  // MigrationSwitch only renders this once the flags have loaded
  const { data: features } = useQuery(queryOptionsGetFeatures());
  const [, activeWorkspaceQuery] = useActiveWorkspace();
  const serviceType = searchParams.get('serviceType');

  return (
    <SingleQueryResults query={activeWorkspaceQuery}>
      {(workspace) => {
        const openPages = STATEMENT_PAGES.filter(
          ({ featureFlag, isEnabled }) =>
            (!featureFlag || features?.[featureFlag] === 'true') &&
            (!isEnabled || isEnabled(workspace)),
        );
        const path =
          openPages.find(({ serviceTypes }) =>
            serviceTypes.some((type) => type === serviceType),
          )?.path ??
          FALLBACK_PAGES.find((page) => openPages.includes(page))?.path ??
          LAST_RESORT_PATH;

        return (
          <NavigateWithParams
            omitParams={['serviceType']}
            replace
            to={`../${path}`}
          />
        );
      }}
    </SingleQueryResults>
  );
};
